/**
 * The body of a region-scoped dashboard page: national, or one region's.
 *
 * One component rather than the same branch in four pages. It owns the three
 * things the branch is not allowed to get wrong:
 *
 *  - It does not render the NATIONAL body until the scope is settled. The
 *    national endpoints are Admin-only, so firing one for a Regional Director
 *    whose own region has not resolved yet is a guaranteed 403.
 *  - It surfaces the READ-ONLY scope. An RD reading a region they hold
 *    `read:all_regions` over but are not assigned to gets `mode: 'read-only'`
 *    back, and the banner is how they are told before they try to change
 *    something the server will refuse. The regional shell used to carry this;
 *    nothing did between its retirement and now.
 *  - It keeps the branch in one place, so the four pages cannot drift apart.
 *
 * The mode comes from the overview envelope — the same "context" call the
 * retired shell made, on the same query key, so a page showing a bucket beside
 * it costs one extra request exactly as it did before.
 */
import type { ReactNode } from 'react';

import { ReadOnlyBanner } from '../ReadOnlyBanner';
import { BucketLoading } from '../regional/BucketStates';
import { useRegionalOverview } from '../regional/regionalApi';

import type { DashboardScope } from './dashboard-scope';

export interface ScopedDashboardBodyProps {
  scope: DashboardScope;
  /** Rendered at national scope. */
  national: ReactNode;
  /** Rendered for a region, given its slug. */
  regional: (regionSlug: string) => ReactNode;
}

export function ScopedDashboardBody({ scope, national, regional }: ScopedDashboardBodyProps) {
  // Unconditional, as hooks must be. It declines to fetch without a slug, so
  // national scope costs nothing.
  const context = useRegionalOverview(scope.regionSlug ?? '');

  if (scope.pending) return <BucketLoading />;
  if (!scope.regionSlug) return <>{national}</>;

  return (
    <>
      {context.data?.mode === 'read-only' && (
        <ReadOnlyBanner regionName={context.data.scope.regionName} />
      )}
      {regional(scope.regionSlug)}
    </>
  );
}
